import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { postAPI } from "../services/api.service";

type InvitePayload = {
  firstname: string;
  lastname: string;
  email: string;
  groupId: string;
};

type UseInviteOpts = {
  groupId: string;
  onSuccess?: (inviteResult?: any) => void;
};

export function useInviteParticipant({ groupId, onSuccess }: UseInviteOpts) {
  const [isOpen, setIsOpen] = useState(false);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    firstname?: string;
    lastname?: string;
    email?: string;
  } | null>(null);

  const toggle = useCallback(() => {
    setErrors(null);
    setIsOpen((o) => !o);
  }, []);

  const resetForm = useCallback(() => {
    setFirstname("");
    setLastname("");
    setEmail("");
    setErrors(null);
  }, []);

  function validate(): boolean {
    const e: any = {};
    if (!firstname || firstname.trim().length < 1)
      e.firstname = "First name is required";
    if (!lastname || lastname.trim().length < 1)
      e.lastname = "Last name is required";
    if (!email || email.trim().length === 0) e.email = "Email is required";
    else {
      // basic email regex
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!re.test(email.trim())) e.email = "Enter a valid email";
    }
    setErrors(Object.keys(e).length ? e : null);
    return Object.keys(e).length === 0;
  }

  const submit = useCallback(async () => {
    if (!validate()) return false;
    setLoading(true);
    try {
      // invitedBy - current user id from localStorage if present
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}") as {
        id?: string;
      } | null;
      const invitedBy = currentUser?.id;

      const payload: InvitePayload = {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        email: email.trim().toLowerCase(),
        groupId,
      };
      // call backend API - adjust URL if your API prefix differs
      const res = await postAPI<{ data: any; message: string }>(
        `groups/invite-participant`,
        { ...payload, invitedBy }
      );

      if (res.status != 200) {
        toast.error("Unable to send invite. Please try again later.");
        setLoading(false);
        return false;
      }

      // success
      toast.success(res?.data?.message || "Invite sent");
      resetForm();
      setIsOpen(false);
      if (onSuccess) onSuccess(res?.data?.data);
      setLoading(false);
      return true;
    } catch (err: any) {
      console.error("Invite API error", err);
      toast.error(err?.message || "Failed to send invite");
      setLoading(false);
      return false;
    }
  }, [firstname, lastname, email, groupId, onSuccess, resetForm]);

  return {
    // state
    isOpen,
    firstname,
    lastname,
    email,
    loading,
    errors,
    // actions
    setFirstname,
    setLastname,
    setEmail,
    toggle,
    resetForm,
    submit,
  };
}
